import { useState, useEffect } from "react"
import { useNavigate, Navigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { PageLoading } from "@/components/PageLoading"
import { useAuth } from "@/hooks/useAuth"
import {
    ChevronRight,
    Smartphone,
    PieChart,
    Zap,
    Check,
    ArrowRight,
    Heart,
    Sparkles,
    ShoppingBag,
    Utensils,
    Layers
} from "lucide-react"
import posthog from 'posthog-js'

export default function LandingPage() {
    const navigate = useNavigate()
    const { data: user, isLoading: authLoading } = useAuth()

    const handleCtaClick = (location: string) => {
        posthog.capture('landing_cta_clicked', { location })
        navigate('/login')
    }
    const [scrolled, setScrolled] = useState(false)

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20)
        }
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id)
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
        }
    }

    if (authLoading) return <PageLoading />
    if (user) return <Navigate to="/dashboard" replace />

    return (
        <div className="min-h-screen bg-[#FFF4EC] text-[#4A3A3A] font-sans selection:bg-[#E86A92]/20 overflow-x-hidden scroll-smooth transition-all duration-700">
            {/* Navigation */}
            <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 px-6 lg:px-12 py-4 ${scrolled ? 'bg-white/80 backdrop-blur-xl shadow-sm' : 'bg-transparent'
                }`}>
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                        <div className="h-10 w-10 bg-white rounded-xl flex items-center justify-center shadow-lg shadow-pink-200/50 rotate-3 border border-pink-100/50">
                            <img src="/icon.svg" alt="Logo" className="w-10 h-10" />
                        </div>
                        <span className="text-xl font-black italic tracking-tight text-[#E86A92]">Cozinha Certa</span>
                    </div>

                    <div className="hidden md:flex items-center gap-8 text-sm font-bold uppercase tracking-widest opacity-80">
                        <button onClick={() => scrollToSection('recursos')} className="hover:text-[#E86A92] transition-colors cursor-pointer">Recursos</button>
                        <button onClick={() => scrollToSection('precos')} className="hover:text-[#E86A92] transition-colors cursor-pointer">Preços</button>
                        <button onClick={() => scrollToSection('sobre')} className="hover:text-[#E86A92] transition-colors cursor-pointer">A Essência</button>
                    </div>

                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/login')}
                            className="text-sm font-bold uppercase tracking-widest hover:text-[#E86A92] transition-colors hidden sm:block cursor-pointer"
                        >
                            Entrar
                        </button>
                        {/* <Button
                            onClick={() => navigate('/login')}
                            className="bg-[#E86A92] hover:bg-[#d4587e] text-white rounded-full px-6 h-11 font-bold shadow-lg shadow-[#E86A92]/20 transition-all active:scale-95"
                        >
                            Começar Grátis
                        </Button> */}
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 px-6 overflow-hidden">
                {/* Background Fluid Shapes */}
                <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[600px] h-[600px] bg-[#F8B6C1]/30 rounded-full blur-3xl opacity-50 pointer-events-none" />
                <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 w-[500px] h-[500px] bg-[#FFD166]/20 rounded-full blur-3xl opacity-50 pointer-events-none" />

                <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
                    <div className="space-y-8 relative z-10 animate-in fade-in slide-in-from-left duration-1000">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/50 backdrop-blur-sm rounded-full border border-[#F8B6C1]/30 shadow-sm">
                            <Sparkles size={16} className="text-[#FFD166]" />
                            <span className="text-xs font-bold uppercase tracking-widest opacity-70">15 Dias Grátis • Sem Compromisso</span>
                        </div>

                        <h1 className="text-5xl lg:text-7xl font-black leading-tight italic">
                            Onde a <span className="text-[#E86A92]">Doçura</span> encontra a <span className="text-[#4A3A3A] underline decoration-[#FFD166] decoration-8 underline-offset-4">Clareza</span>.
                        </h1>

                        <p className="text-lg lg:text-xl opacity-80 leading-relaxed max-w-xl">
                            Transforme o caos da cozinha em uma ordem poética. Gerencie pedidos, materiais e receitas com a leveza de um merengue e a precisão de um mestre chocolatier.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <Button
                                onClick={() => handleCtaClick('hero')}
                                className="h-16 px-10 rounded-2xl bg-[#E86A92] hover:bg-[#d4587e] text-lg font-bold shadow-xl shadow-[#E86A92]/30 group transition-all cursor-pointer"
                            >
                                Criar Minha Loja
                                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                            </Button>
                        </div>

                        {/* <div className="flex items-center gap-6 pt-4 grayscale opacity-60">
                            <div className="flex items-center gap-1 font-bold">
                                <Star fill="#FFD166" className="text-[#FFD166]" size={18} />
                                <Star fill="#FFD166" className="text-[#FFD166]" size={18} />
                                <Star fill="#FFD166" className="text-[#FFD166]" size={18} />
                                <Star fill="#FFD166" className="text-[#FFD166]" size={18} />
                                <Star fill="#FFD166" className="text-[#FFD166]" size={18} />
                                <span className="ml-2 text-sm uppercase tracking-tighter pt-1">4.9/5 por confeiteiros</span>
                            </div>
                        </div> */}
                    </div>

                    <div className="relative animate-in fade-in slide-in-from-right duration-1000">
                        <div className="relative z-10 rounded-[3rem] overflow-hidden shadow-2xl shadow-pink-200/50 rotate-2 hover:rotate-0 transition-transform duration-700">
                            <img
                                src="/hero.png"
                                alt="Cozinha Certa Hero"
                                className="w-full h-auto scale-105"
                            />
                        </div>
                        {/* Decorative Organic Blobs */}
                        <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#F8B6C1] rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-blob" />
                        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-[#FFD166] rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-blob" />
                    </div>
                </div>
            </section>

            {/* Narrative Section: The Chaos to Order */}
            <section id="sobre" className="py-24 bg-white relative overflow-hidden">
                <div className="max-w-5xl mx-auto px-6 text-center space-y-12">
                    <div className="inline-block px-4 py-1.5 bg-[#FFF4EC] rounded-full text-xs font-black uppercase tracking-[0.2em] text-[#E86A92]">
                        A Essência
                    </div>
                    <h2 className="text-4xl lg:text-6xl font-black italic">
                        Chega de notas perdidas e <br />
                        <span className="text-[#E86A92] opacity-40 italic">sobras de ingredientes.</span>
                    </h2>
                    <p className="text-xl lg:text-2xl text-slate-500 max-w-3xl mx-auto leading-relaxed italic">
                        "Desenhamos a Cozinha Certa como um santuário digital. Onde antes havia o estresse das encomendas via chat, agora existe a fluidez de uma gestão que entende o seu tempo e valoriza sua arte."
                    </p>
                    <div className="grid md:grid-cols-3 gap-8 pt-12">
                        <div className="space-y-4 p-8 rounded-3xl bg-[#FFF4EC]/50 border border-slate-50">
                            <div className="h-12 w-12 bg-white rounded-2xl flex items-center justify-center text-[#E86A92] shadow-sm mx-auto">
                                <ShoppingBag size={24} />
                            </div>
                            <h3 className="font-bold text-xl">Pedidos Sem Ruído</h3>
                            <p className="text-sm opacity-70">Sua própria vitrine digital, onde clientes pedem e você apenas executa.</p>
                        </div>
                        <div className="space-y-4 p-8 rounded-3xl bg-[#FFF4EC]/80 border border-slate-100 scale-105 shadow-xl shadow-pink-100/20">
                            <div className="h-12 w-12 bg-white rounded-2xl flex items-center justify-center text-[#E86A92] shadow-sm mx-auto">
                                <PieChart size={24} />
                            </div>
                            <h3 className="font-bold text-xl">Lucro Real</h3>
                            <p className="text-sm opacity-70">Saiba exatamente quanto custa cada grama de farinha e cada gota de essência.</p>
                        </div>
                        <div className="space-y-4 p-8 rounded-3xl bg-[#FFF4EC]/50 border border-slate-50">
                            <div className="h-12 w-12 bg-white rounded-2xl flex items-center justify-center text-[#E86A92] shadow-sm mx-auto">
                                <Smartphone size={24} />
                            </div>
                            <h3 className="font-bold text-xl">Gestão na Mão</h3>
                            <p className="text-sm opacity-70">Funciona perfeitamente no seu celular, direto da bancada de produção.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Detail */}
            <section id="recursos" className="py-12 md:py-24 bg-[#FFF4EC]/30">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                            <div className="space-y-3 md:space-y-4">
                                <div className="bg-white rounded-2xl md:rounded-[2rem] p-4 md:p-6 shadow-sm border border-slate-100 flex flex-row md:flex-col items-center md:items-start md:justify-between gap-4 md:aspect-square">
                                    <Utensils className="text-[#E86A92]" size={24} />
                                    <h4 className="font-bold text-sm md:text-base">Fichas Técnicas</h4>
                                </div>
                                <div className="bg-[#F8B6C1]/20 rounded-2xl md:rounded-[2rem] p-4 md:p-6 border border-[#F8B6C1]/30 flex flex-row md:flex-col items-center md:items-start md:justify-between gap-4 md:aspect-square">
                                    <Layers className="text-[#E86A92]" size={24} />
                                    <h4 className="font-bold text-[#E86A92] text-sm md:text-base">Estoque Inteligente</h4>
                                </div>
                            </div>
                            <div className="space-y-3 md:space-y-4">
                                <div className="bg-[#FFD166]/20 rounded-2xl md:rounded-[2rem] p-4 md:p-6 border border-[#FFD166]/30 flex flex-row md:flex-col items-center md:items-start md:justify-between gap-4 md:aspect-square">
                                    <Zap className="text-[#4A3A3A]" size={24} />
                                    <h4 className="font-bold text-sm md:text-base">IA de Notas</h4>
                                </div>
                                <div className="bg-white rounded-2xl md:rounded-[2rem] p-4 md:p-6 shadow-sm border border-slate-100 flex flex-row md:flex-col items-center md:items-start md:justify-between gap-4 md:aspect-square">
                                    <Check className="text-[#E86A92]" size={24} />
                                    <h4 className="font-bold text-sm md:text-base">Acesso no Celular</h4>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-8">
                            <div className="inline-block px-4 py-1.5 bg-[#F8B6C1]/30 rounded-full text-xs font-black uppercase tracking-[0.2em] text-[#E86A92]">
                                Funcionalidades
                            </div>
                            <h2 className="text-2xl md:text-4xl lg:text-5xl font-black italic leading-tight">
                                Criado por quem entende de <span className="text-[#E86A92]">confeitaria artesanal.</span>
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
                                {[
                                    { title: "Leitor de Notas fiscais com IA", desc: "Tire uma foto e a IA importa seus materiais automaticamente." },
                                    { title: "Cálculo de Receitas em Tempo Real", desc: "Preços atualizados conforme os custos de mercado mudam." },
                                    { title: "Vitrine de Pedidos Exclusiva", desc: "O seu link personalizado para receber pedidos organizados." },
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4 items-start group">
                                        <div className="h-8 w-8 rounded-full bg-white flex-shrink-0 flex items-center justify-center text-[#E86A92] shadow-sm group-hover:bg-[#E86A92] group-hover:text-white transition-all">
                                            <Check size={16} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-lg">{item.title}</h4>
                                            <p className="text-sm opacity-60">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Button
                                onClick={() => handleCtaClick('features')}
                                variant="link"
                                className="p-0 h-auto text-[#E86A92] font-black uppercase tracking-widest text-xs flex items-center gap-2"
                            >
                                Começar teste gratuíto <ChevronRight size={14} />
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="precos" className="py-32 bg-white relative">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-screen-xl h-full bg-[#FFF4EC]/20 rounded-[100px] pointer-events-none" />

                <div className="max-w-7xl mx-auto px-6 relative z-10 text-center space-y-16">
                    <div className="space-y-4">
                        <h2 className="text-4xl lg:text-6xl font-black italic">
                            Um plano simples <br />
                            <span className="text-[#E86A92]">pelo preço de um café.</span>
                        </h2>
                        <p className="text-slate-500 max-w-xl mx-auto">
                            Foque na sua arte, nós cuidamos da gestão. Sem taxas escondidas, sem contratos longos.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-stretch">
                        {/* Free Trial Card */}
                        <div className="bg-white/50 backdrop-blur-sm rounded-[3rem] p-10 border-2 border-dashed border-[#FFD166] flex flex-col justify-between items-center text-center space-y-6 hover:bg-white transition-all group">
                            <div className="space-y-2">
                                <div className="h-16 w-16 bg-[#FFD166]/20 rounded-2xl flex items-center justify-center text-[#4A3A3A] mx-auto mb-4 group-hover:rotate-6 transition-transform">
                                    <Sparkles size={32} />
                                </div>
                                <h3 className="text-2xl font-black italic">Teste Gratuito</h3>
                                <p className="text-4xl font-black text-[#4A3A3A]">15 Dias</p>
                                <p className="text-sm opacity-60">Experimente todos os recursos sem gastar nada.</p>
                            </div>

                            <ul className="space-y-3 text-left w-full py-4 opacity-70">
                                <li className="flex items-center gap-2 text-sm font-medium">
                                    <Check size={14} className="text-[#FFD166]" /> Acesso Total
                                </li>
                                <li className="flex items-center gap-2 text-sm font-medium">
                                    <Check size={14} className="text-[#FFD166]" /> Importação de Notas
                                </li>
                                {/* <li className="flex items-center gap-2 text-sm font-medium">
                                    <Check size={14} className="text-[#FFD166]" /> Sem Cartão Requerido
                                </li> */}
                            </ul>

                            <Button
                                onClick={() => handleCtaClick('pricing_free')}
                                variant="outline"
                                className="w-full h-14 rounded-2xl border-2 border-slate-200 hover:border-[#FFD166] hover:bg-[#FFD166]/10 font-bold transition-all cursor-pointer"
                            >
                                Começar Teste
                            </Button>
                        </div>

                        {/* Pro Plan Card */}
                        <div className="bg-white rounded-[3rem] p-10 shadow-2xl shadow-pink-100/50 border-4 border-[#FFF4EC] relative overflow-hidden group flex flex-col justify-between">
                            {/* Decorative Wave */}
                            <div className="absolute top-0 right-0 p-8 opacity-10 text-[#E86A92]">
                                <Heart size={80} />
                            </div>

                            <div className="relative z-10 space-y-8">
                                <div className="space-y-2">
                                    <span className="text-sm font-black uppercase tracking-widest text-[#E86A92]">Plano Pro</span>
                                    <div className="flex items-center justify-center gap-2">
                                        <span className="text-xl font-bold opacity-40">R$</span>
                                        <span className="text-7xl font-black tracking-tighter italic">29<span className="text-2xl opacity-40">,90</span></span>
                                    </div>
                                </div>

                                <ul className="space-y-4 text-left border-y border-slate-50 py-8">
                                    {[
                                        "Pedidos Ilimitados",
                                        "Importação de Notas com IA",
                                        "Custo de Materiais em Tempo Real",
                                        "Dashboard de Lucratividade",
                                        "Suporte Prioritário",
                                    ].map((feat, i) => (
                                        <li key={i} className="flex items-center gap-3 text-sm font-medium">
                                            <div className="h-5 w-5 rounded-full bg-pink-50 flex items-center justify-center text-[#E86A92]">
                                                <Check size={12} />
                                            </div>
                                            {feat}
                                        </li>
                                    ))}
                                </ul>

                                <Button
                                    onClick={() => handleCtaClick('pricing_pro')}
                                    className="w-full h-16 rounded-2xl bg-[#E86A92] hover:bg-[#d4587e] text-lg font-bold shadow-xl shadow-[#E86A92]/20 transition-all active:scale-95 cursor-pointer"
                                >
                                    Assinar Agora
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA Footer */}
            <footer className="bg-[#4A3A3A] text-[#FFF4EC] pt-24 pb-12 px-6 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#E86A92]/10 rounded-full blur-[100px]" />

                <div className="max-w-7xl mx-auto space-y-20 relative z-10">
                    <div className="flex flex-col lg:flex-row gap-16 items-center justify-between">
                        <div className="space-y-6 text-center lg:text-left">
                            <h2 className="text-4xl lg:text-7xl font-black italic leading-tight">
                                Retome o prazer<br /> de <span className="text-[#F8B6C1]">criar.</span>
                            </h2>
                            <p className="text-lg opacity-60 max-w-lg">
                                Deixe a complexidade técnica conosco. Sua doçura merece o palco principal.
                            </p>
                            <Button
                                onClick={() => handleCtaClick('footer')}
                                className="h-14 px-10 rounded-2xl bg-[#FFD166] hover:bg-[#f5c34e] text-[#4A3A3A] font-bold text-lg shadow-xl shadow-[#FFD166]/10 cursor-pointer"
                            >
                                Ativar Meus 15 Dias Grátis
                            </Button>
                        </div>

                        <div className="flex flex-col items-center gap-4">
                            <div className="h-20 w-20 bg-white/10 backdrop-blur-xl rounded-2xl flex items-center justify-center border border-white/10 rotate-6">

                                <img src="/icon.svg" alt="Logo" className="w-10 h-10" />

                            </div>
                            <span className="text-2xl font-black italic tracking-tight text-[#F8B6C1]">Cozinha Certa</span>
                        </div>
                    </div>

                    <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-8 opacity-40 text-xs font-bold uppercase tracking-widest">
                        <p>© 2026 Cozinha Certa. O santuário dos confeiteiros.</p>
                        <div className="flex gap-8">
                            <button onClick={() => navigate('/termos')} className="hover:text-white transition-colors cursor-pointer">Termos</button>
                            <button onClick={() => navigate('/privacidade')} className="hover:text-white transition-colors cursor-pointer">Privacidade</button>
                            <a href="#" className="hover:text-white transition-colors cursor-pointer">Ajuda</a>
                        </div>
                    </div>
                </div>
            </footer>

            <style>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob {
                    animation: blob 7s infinite;
                }
                .animation-delay-2000 {
                    animation-delay: 2s;
                }
            `}</style>
        </div>
    )
}
